import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ErrorState, Spinner } from '@/components/ui';
import { settingsApi } from '@/api/settings.api';
import { useSession } from './session';
import { landsOnCalendar } from './landing';
import { AppLayout } from './AppLayout';
import { LoginPage } from '@/features/auth/LoginPage';
import { ForbiddenPage, NotFoundPage } from './ErrorPages';
import { useTranslation } from 'react-i18next';

// Split per route: a receptionist who never opens Reports should not download
// them, and the calendar is the only screen that has to be fast on first load.
/* Pagina invitației: cerută rar, deci nu intră în pachetul de pornire. */
const InvitationPage = lazy(() =>
  import('@/features/auth/InvitationPage').then((m) => ({ default: m.InvitationPage })),
);
/* Studiul partajat: se deschide fără cont, cu jetonul din adresă. */
const SharedStudyPage = lazy(() => import('@/features/imaging/SharedStudyPage'));
const DashboardPage = lazy(() => import('@/features/dashboard/DashboardPage'));
const StartPage = lazy(() =>
  import('@/features/dashboard/start/StartPage').then((m) => ({ default: m.StartPage })),
);
const CalendarPage = lazy(() => import('@/features/calendar/CalendarPage'));
const EconomyPage = lazy(() => import('@/features/economy/EconomyPage'));
const AccountingPage = lazy(() => import('@/features/accounting/AccountingPage'));
const PatientsPage = lazy(() => import('@/features/patients/PatientsPage'));
const PatientRecordPage = lazy(() => import('@/features/patients/PatientRecordPage'));
const InventoryPage = lazy(() => import('@/features/inventory/InventoryPage'));
const PaymentsPage = lazy(() => import('@/features/payments/PaymentsPage'));
const LabPage = lazy(() => import('@/features/lab/LabPage'));
const HrPage = lazy(() => import('@/features/hr/HrPage'));
const SettingsPage = lazy(() => import('@/features/settings/SettingsPage'));
const JournalPage = lazy(() => import('@/features/journal/JournalPage'));
const MfaEnrollmentPage = lazy(() => import('@/features/auth/MfaEnrollmentPage'));
const ReportsPage = lazy(() => import('@/features/reports/ReportsPage'));
const WaitlistPage = lazy(() => import('@/features/waitlist/WaitlistPage'));
const InquiriesPage = lazy(() => import('@/features/inquiries/InquiriesPage'));
const ChatPage = lazy(() => import('@/features/chat/ChatPage'));
const PortalApp = lazy(() => import('@/features/portal/PortalApp'));
const VisitsPage = lazy(() => import('@/features/visits/VisitsPage'));
const TreatmentPage = lazy(() => import('@/features/patients/TreatmentPage'));
const StudyPage = lazy(() => import('@/features/imaging/StudyPage'));
const ImagingPatientPage = lazy(() => import('@/features/imaging/ImagingPatientPage'));
const ImagingPage = lazy(() => import('@/features/imaging/ImagingPage'));

/** Route definitions, each with the permission that gates it. */
export interface RouteMeta {
  path: string;
  label: string;
  permission?: string;
  element: React.ReactNode;
}

function Loading() {
  return (
    <div style={{ display: 'grid', placeItems: 'center', padding: 'var(--s-8)' }}>
      <Spinner size={24} />
    </div>
  );
}

/**
 * The screen a sign-in lands on, chosen by the clinic in Setări → Personalizare.
 *
 * The Panou by default, as it always was. A clinic whose front desk lives in
 * the calendar can make that the landing screen instead — but only for someone
 * who may open it (`programari.vede`); for anyone else it stays the Panou, which
 * everyone can see. Read from `GET /settings/look`, so reception and doctors —
 * who lack `setari.vede` — get the clinic's choice too.
 *
 * Only the landing itself: the „Panou" link and the `d` key say so in the
 * navigation state (`landing.ts`) and get the Panou whatever the setting.
 */
function LandingRoute() {
  const { can } = useSession();
  const location = useLocation();
  const look = useQuery({
    queryKey: ['settings', 'look'],
    queryFn: settingsApi.look,
    staleTime: 5 * 60_000,
  });
  if (
    landsOnCalendar({
      landingScreen: look.data?.landingScreen,
      canSeeCalendar: can('programari.vede'),
      navigationState: location.state,
    })
  ) {
    return <Navigate to="/calendar" replace />;
  }
  return (
    <Suspense fallback={<Loading />}>
      <DashboardPage />
    </Suspense>
  );
}

/**
 * Wraps a route in its permission and feature checks.
 *
 * This hides a screen the user cannot use; it is not the security boundary.
 * The API enforces the same permission on every request, so bypassing this
 * gets an empty screen and a 403, not data.
 *
 * `feature` is checked as well as `permission`, and on the route rather than
 * only in the sidebar — hiding a link leaves the URL working, so a clinic with
 * the laboratory switched off could still reach it by typing the address or
 * following an old bookmark.
 */
/**
 * `permission` may be one key or several. Several means **any of them** —
 * the same rule the API's `@RequireAnyPermission` applies.
 *
 * That option exists because its absence was a live defect. The patients
 * screen requires either `pacienti.vede_toti` or `pacienti.vede_proprii` on
 * the server, but the route could only name one, and it named the second. So
 * the sidebar — which checks `vede_toti` — offered "Pacienți" to reception,
 * assistants, accountants, read-only staff and the clinic owner, and the
 * route then refused all five with "Nu ai acces la această secțiune". Five of
 * the nine system roles, on the screen the practice uses most.
 *
 * The lesson is narrower than "test the roles": a guard on the client that
 * names a *different* key from the server is not a stricter guard, it is a
 * wrong one. The nav, the route and the controller have to agree, and the
 * controller is what decides.
 */
function Guarded({
  permission,
  feature,
  children,
}: {
  permission?: string | string[];
  feature?: string;
  children: React.ReactNode;
}) {
  const { can, hasFeature } = useSession();
  if (feature && !hasFeature(feature)) return <Navigate to="/" replace />;

  const needed = permission === undefined ? [] : [permission].flat();
  if (needed.length > 0 && !needed.some((p) => can(p))) {
    return <ForbiddenPage permission={needed[0]!} />;
  }
  return <>{children}</>;
}

export function AppRoutes() {
  const { t } = useTranslation('common');
  const { user, status, hasFeature, logout } = useSession();

  if (status === 'loading') return <Loading />;
  if (!user) {
    /* Invitația se deschide **fără** cont — asta e tot rostul ei. Se citește
       din adresă, nu dintr-o rută, fiindcă rutele aplicației nu se montează
       până nu e cineva autentificat, iar un coleg invitat nu e încă. */
    const invited = window.location.pathname.match(/^\/invitatie\/([^/]+)\/?$/);
    if (invited) {
      return (
        <Suspense fallback={<Loading />}>
          <InvitationPage token={decodeURIComponent(invited[1])} />
        </Suspense>
      );
    }

    /* Linkul de imagistică, la fel: destinatarul e un medic trimițător sau un
       laborator, care n-are și n-are de ce să aibă cont. */
    const sharedStudy = window.location.pathname.match(/^\/imagistica\/([^/]+)\/?$/);
    if (sharedStudy) {
      return (
        <Suspense fallback={<Loading />}>
          <SharedStudyPage token={decodeURIComponent(sharedStudy[1])} />
        </Suspense>
      );
    }
    return <LoginPage />;
  }

  // A patient signs in through the same page and lands somewhere else
  // entirely. Not a narrowed clinic app: the API refuses a patient token on
  // every staff route, so rendering the clinic shell would be a screen of
  // 403s rather than a portal.
  if (user.kind === 'patient') {
    // A clinic without the portal: every portal route answers 404 at the API
    // (FeatureGuard), so the portal would open on a screen of errors. Say what
    // is true instead, and let the patient leave.
    if (!hasFeature('patientPortal')) {
      return (
        <div
          style={{ display: 'grid', placeItems: 'center', height: '100%', padding: 'var(--s-6)' }}
        >
          <div>
            <ErrorState
              title={t('portalulPacientuluiNuEste')}
              message={t('clinicaNuArePortalul')}
            />
            <button type="button" className="btn" onClick={() => void logout()}>
              {t('iesiDinCont')}
            </button>
          </div>
        </div>
      );
    }
    return (
      <Suspense fallback={<Loading />}>
        <PortalApp />
      </Suspense>
    );
  }

  // SEC-16: a role requires two-step sign-in this account has not set up. The
  // API answers only the enrolment routes until it does, so the clinic shell
  // would be a screen of refusals — open the enrolment screen alone.
  if (user.mfaEnrollmentRequired) {
    return (
      <Suspense fallback={<Loading />}>
        <MfaEnrollmentPage />
      </Suspense>
    );
  }

  return (
    <Routes>
      <Route element={<AppLayout />}>
        {/* The landing screen. Available to everyone signed in; the figures
            on it are narrowed to what the caller may see. */}
        <Route index element={<LandingRoute />} />

        <Route
          path="calendar/*"
          element={
            <Guarded permission="programari.vede">
              <Suspense fallback={<Loading />}>
                <CalendarPage />
              </Suspense>
            </Guarded>
          }
        />

        {/* The list pane lives on the parent route so it stays mounted (with
            its search and scroll) while records open and close beside it. */}
        <Route
          path="economy"
          element={
            <Guarded permission="financiar.vede" feature="economy">
              <Suspense fallback={<Loading />}>
                <EconomyPage />
              </Suspense>
            </Guarded>
          }
        />

        {/* Contabilitatea: registrul, balanța, închiderea lunii, exportul,
            facturile fiscale și extrasele. Fiecare controller cere
            `contabilitate.vede` pentru citire. */}
        <Route
          path="accounting"
          element={
            <Guarded permission="contabilitate.vede" feature="accounting">
              <Suspense fallback={<Loading />}>
                <AccountingPage />
              </Suspense>
            </Guarded>
          }
        />

        {/* Analytics is the „Privire generală" tab of Rapoarte since 16.09;
            a bookmark or an old link to it still arrives there. */}
        <Route
          path="analytics"
          element={
            <Guarded permission="rapoarte.vede" feature="reports">
              <Navigate to="/reports?tab=general" replace />
            </Guarded>
          }
        />

        <Route
          path="patients"
          element={
            <Guarded permission={['pacienti.vede_toti', 'pacienti.vede_proprii']}>
              <Suspense fallback={<Loading />}>
                <PatientsPage />
              </Suspense>
            </Guarded>
          }
        >
          <Route
            path=":patientId"
            element={
              <Guarded permission="fisa.vede">
                <Suspense fallback={<Loading />}>
                  <PatientRecordPage />
                </Suspense>
              </Guarded>
            }
          />
        </Route>

        <Route
          path="patients/:patientId/visits"
          element={
            <Guarded permission="fisa.vede">
              <Suspense fallback={<Loading />}>
                <VisitsPage />
              </Suspense>
            </Guarded>
          }
        />

        {/* Tratamentul pe pagina lui: ușa recepției, care completează vizita și o
            leagă de plan fără să vadă fișa clinică (13 septembrie). Serverul
            răspunde aici doar cu schema, rândurile planului și zilele vizitelor. */}
        <Route
          path="patients/:patientId/treatment"
          element={
            <Guarded permission={['fisa.vede', 'tratament.finalizeaza']}>
              <Suspense fallback={<Loading />}>
                <TreatmentPage />
              </Suspense>
            </Guarded>
          }
        />

        {/* Linkul partajat merge și pentru cineva autentificat: medicul care
            l-a făcut îl deschide ca să vadă ce a trimis, iar fără ruta asta ar
            primi „pagina nu există" pe propriul link. */}
        <Route
          path="imagistica/:token"
          element={
            <Suspense fallback={<Loading />}>
              <SharedStudyRoute />
            </Suspense>
          }
        />

        <Route
          path="imaging"
          element={
            <Guarded permission="imagistica.vede" feature="imaging">
              <Suspense fallback={<Loading />}>
                <ImagingPage />
              </Suspense>
            </Guarded>
          }
        />

        {/* Profilul de imagistică al pacientului, pe toată lățimea, ca la referință. */}
        <Route
          path="imaging/patients/:patientId"
          element={
            <Guarded permission="imagistica.vede" feature="imaging">
              <Suspense fallback={<Loading />}>
                <ImagingPatientPage />
              </Suspense>
            </Guarded>
          }
        />

        {/* Un studiu imagistic, pe tot ecranul. Sub pacient în adresă, ca
            întoarcerea din browser să ducă înapoi în fișă. */}
        <Route
          path="patients/:patientId/studies/:studyId"
          element={
            <Guarded permission="imagistica.vede" feature="imaging">
              <Suspense fallback={<Loading />}>
                <StudyPage />
              </Suspense>
            </Guarded>
          }
        />

        {/* Pornirea ghidată: ce are de făcut o clinică nouă, în ordine, într-un
            loc. Nu e un modul — e drumul până la a avea unul. */}
        <Route
          path="start"
          element={
            <Guarded permission="setari.modifica">
              <Suspense fallback={<Loading />}>
                <StartPage />
              </Suspense>
            </Guarded>
          }
        />
        <Route
          path="waitlist"
          element={
            <Guarded permission="programari.vede" feature="waitlist">
              <Suspense fallback={<Loading />}>
                <WaitlistPage />
              </Suspense>
            </Guarded>
          }
        />
        <Route
          path="inquiries"
          element={
            <Guarded permission="cereri.vede" feature="patientInquiries">
              <Suspense fallback={<Loading />}>
                <InquiriesPage />
              </Suspense>
            </Guarded>
          }
        />

        <Route
          path="payments/*"
          element={
            <Guarded permission="incasari.vede">
              <Suspense fallback={<Loading />}>
                <PaymentsPage />
              </Suspense>
            </Guarded>
          }
        />

        <Route
          path="inventory/*"
          element={
            <Guarded permission="stoc.vede" feature="inventory">
              <Suspense fallback={<Loading />}>
                <InventoryPage />
              </Suspense>
            </Guarded>
          }
        />

        <Route
          path="lab/*"
          element={
            <Guarded permission="laborator.vede" feature="dentalLab">
              <Suspense fallback={<Loading />}>
                <LabPage />
              </Suspense>
            </Guarded>
          }
        />

        <Route
          path="hr/*"
          element={
            <Guarded
              permission={[
                'concedii.cere',
                'concedii.vede',
                'personal.vede',
                'pontaj.vede',
                'salarii.vede',
                'recrutare.gestioneaza',
              ]}
              feature="hr"
            >
              <Suspense fallback={<Loading />}>
                <HrPage />
              </Suspense>
            </Guarded>
          }
        />

        <Route
          path="settings/*"
          element={
            // Toată lumea intră: filele clinicii cer `setari.vede` una câte una,
            // cele personale (contul, programul, aspectul) sunt ale fiecăruia.
            <Guarded>
              <Suspense fallback={<Loading />}>
                <SettingsPage />
              </Suspense>
            </Guarded>
          }
        />

        <Route
          path="chat"
          element={
            <Guarded permission="chat.foloseste" feature="teamChat">
              <Suspense fallback={<Loading />}>
                <ChatPage />
              </Suspense>
            </Guarded>
          }
        />

        <Route
          path="reports"
          element={
            <Guarded permission="rapoarte.vede" feature="reports">
              <Suspense fallback={<Loading />}>
                <ReportsPage />
              </Suspense>
            </Guarded>
          }
        />

        <Route
          path="journal"
          element={
            <Guarded permission="jurnal.vede">
              <Suspense fallback={<Loading />}>
                <JournalPage />
              </Suspense>
            </Guarded>
          }
        />

        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}

/**
 * Studiul partajat, când cel care deschide linkul e autentificat.
 *
 * Jetonul vine din adresă, la fel; ce se vede e tot ce vede un străin — deci
 * medicul poate verifica exact ce a trimis, nu o variantă mai bogată a
 * aceleiași pagini.
 */
function SharedStudyRoute() {
  const { token = '' } = useParams();
  return <SharedStudyPage token={decodeURIComponent(token)} />;
}
